import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { MedicineResponseDto } from "libs/cellera-sdk";
import { useCallback, useState, useEffect } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface MedicineDetailsDialogProps {
  open: boolean;
  medicine: MedicineResponseDto | null;
  onClose: () => void;
  onAddToCart: (sig: string, dispense: number, refill: number) => void;
}

const formatActiveIngredients = (medicine: MedicineResponseDto) => {
  if (!medicine.activeIngredients || medicine.activeIngredients.length === 0) {
    return "N/A";
  }
  return medicine.activeIngredients
    .map((ing) => `${ing.name} ${ing.strength}`)
    .join("; ");
};

function MedicineDetailsDialog({
  open,
  medicine,
  onClose,
  onAddToCart,
}: MedicineDetailsDialogProps) {
  const { colors } = useCelleraTheme();

  const [sig, setSig] = useState("");
  const [dispense, setDispense] = useState("1");
  const [refill, setRefill] = useState("0");

  // Reset form when medicine changes or dialog opens/closes
  useEffect(() => {
    if (open && medicine) {
      setSig("");
      setDispense("1");
      setRefill("0");
    }
  }, [open, medicine]);

  const handleDispenseChange = useCallback((value: string) => {
    // Only allow numeric characters, max 4 digits
    if (value === "" || /^\d{0,4}$/.test(value)) {
      setDispense(value);
    }
  }, []);

  const handleDispenseBlur = useCallback(() => {
    const numValue = parseInt(dispense);
    if (!dispense || isNaN(numValue) || numValue <= 0) {
      setDispense("1");
    }
  }, [dispense]);

  const handleRefillChange = useCallback((value: string) => {
    // Only allow numeric characters, max 4 digits
    if (value === "" || /^\d{0,4}$/.test(value)) {
      setRefill(value);
    }
  }, []);

  const handleRefillBlur = useCallback(() => {
    const numValue = parseInt(refill);
    if (!refill || isNaN(numValue) || numValue < 0) {
      setRefill("0");
    }
  }, [refill]);

  const handleIncrement = useCallback(
    (field: "dispense" | "refill") => {
      if (field === "dispense") {
        const current = parseInt(dispense) || 1;
        if (current < 9999) {
          setDispense(String(current + 1));
        }
      } else {
        const current = parseInt(refill) || 0;
        if (current < 9999) {
          setRefill(String(current + 1));
        }
      }
    },
    [dispense, refill]
  );

  const handleDecrement = useCallback(
    (field: "dispense" | "refill") => {
      if (field === "dispense") {
        const current = parseInt(dispense) || 1;
        if (current > 1) {
          setDispense(String(current - 1));
        }
      } else {
        const current = parseInt(refill) || 0;
        if (current > 0) {
          setRefill(String(current - 1));
        }
      }
    },
    [dispense, refill]
  );

  const handleAddToCart = useCallback(() => {
    if (!medicine || !sig.trim()) {
      return;
    }

    onAddToCart(sig.trim(), parseInt(dispense) || 1, parseInt(refill) || 0);
    onClose();
  }, [medicine, sig, dispense, refill, onAddToCart, onClose]);

  const isAddToCartDisabled = !medicine || !sig.trim();

  if (!medicine) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${colors.border.main}`,
          backgroundColor: colors.background.secondary,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          Medicine Details
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Brand Name */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Brand Name
            </Typography>
            <Typography variant="body2">
              {medicine.brandName || "N/A"}
            </Typography>
          </Box>

          {/* Labeller */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Labeller
            </Typography>
            <Typography variant="body2">
              {medicine.labelerName || "N/A"}
            </Typography>
          </Box>

          {/* Active Ingredients */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Active Ingredient(s)
            </Typography>
            <Typography variant="body2">
              {formatActiveIngredients(medicine)}
            </Typography>
          </Box>

          {/* Dosage Form */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Dosage Form
            </Typography>
            <Typography variant="body2">
              {medicine.dosageForm || "N/A"}
            </Typography>
          </Box>

          {/* Route */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Route
            </Typography>
            <Typography variant="body2">
              {medicine.route?.join("; ") || "N/A"}
            </Typography>
          </Box>

          {/* Package */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Package
            </Typography>
            <Typography variant="body2">
              {medicine.description || "N/A"}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* SIG (Instruction) */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              SIG (Instruction){" "}
              <span style={{ color: colors.error[500] }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Dose/Unit/Route/Frequency/Direction/Duration"
              value={sig}
              onChange={(e) => {
                const value = e.target.value;
                // Printable ASCII characters only, max 255
                if (value.length <= 255 && /^[\x20-\x7E]*$/.test(value)) {
                  setSig(value);
                }
              }}
              multiline
              rows={2}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.background.paper,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, fontSize: "0.7rem" }}
            >
              {sig.length}/255 characters
            </Typography>
          </Box>

          {/* Dispense */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Dispense <span style={{ color: colors.error[500] }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDecrement("dispense")}
                disabled={parseInt(dispense) <= 1}
                sx={{
                  minWidth: 36,
                  px: 1,
                  borderColor: colors.border.main,
                }}
              >
                <RemoveIcon fontSize="small" />
              </Button>
              <TextField
                size="small"
                type="text"
                value={dispense}
                onChange={(e) => handleDispenseChange(e.target.value)}
                onBlur={handleDispenseBlur}
                sx={{
                  width: 80,
                  "& input": {
                    textAlign: "center",
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleIncrement("dispense")}
                disabled={parseInt(dispense) >= 9999}
                sx={{
                  minWidth: 36,
                  px: 1,
                  borderColor: colors.border.main,
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
          </Box>

          {/* Refill */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Refill <span style={{ color: colors.error[500] }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDecrement("refill")}
                disabled={parseInt(refill) <= 0}
                sx={{
                  minWidth: 36,
                  px: 1,
                  borderColor: colors.border.main,
                }}
              >
                <RemoveIcon fontSize="small" />
              </Button>
              <TextField
                size="small"
                type="text"
                value={refill}
                onChange={(e) => handleRefillChange(e.target.value)}
                onBlur={handleRefillBlur}
                sx={{
                  width: 80,
                  "& input": {
                    textAlign: "center",
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleIncrement("refill")}
                disabled={parseInt(refill) >= 9999}
                sx={{
                  minWidth: 36,
                  px: 1,
                  borderColor: colors.border.main,
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.border.main}`,
          p: 2,
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            borderColor: colors.border.main,
            color: colors.text.primary,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddToCart}
          disabled={isAddToCartDisabled}
          sx={{
            textTransform: "none",
            backgroundColor: colors.secondary.main,
            "&:hover": {
              backgroundColor: colors.secondary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          Add to Order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MedicineDetailsDialog;
